import React, { useEffect, useState } from "react";
import { DebounceInput } from "react-debounce-input";
import { toast } from "react-hot-toast";
import { Link } from "react-router-dom";
import { FaPlus, FaExternalLinkAlt, FaBook, FaLink, FaVideo, FaChalkboardTeacher, FaNewspaper, FaTrash, FaEdit, FaPalette } from "react-icons/fa";
import { HiOutlineDocumentText } from "react-icons/hi2";
import { useSelector } from "react-redux";

import Loader from "@/components/loader";
import Modal from "@/components/modal";
import Pagination from "@/components/pagination";
import api from "@/services/api";

import placeholder from "@/assets/learn/placeholder.png";

export default function Resources() {
  const [search, setSearch] = useState("");
  const [refresh, setRefesh] = useState(0);
  const [filterType, setFilterType] = useState("");

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Resources & Inspiration</h1>
          <p className="text-gray-600 mt-1">Curated books, articles, videos, and tutorials to help you grow.</p>
        </div>
        <CreateResourceButton onCreated={() => setRefesh((prev) => prev + 1)} />
      </div>

      <div className="mb-8 space-y-4">
        <DebounceInput
          debounceTimeout={500}
          className="w-full max-w-md px-4 py-2 rounded-xl border border-gray-200 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
          placeholder="Search resources..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        
        <div className="flex flex-wrap gap-2">
           <FilterButton active={filterType === ""} onClick={() => setFilterType("")}>All</FilterButton>
           <FilterButton active={filterType === "tutorial"} onClick={() => setFilterType("tutorial")}>Tutorials</FilterButton>
           <FilterButton active={filterType === "component"} onClick={() => setFilterType("component")}>Components</FilterButton>
           <FilterButton active={filterType === "book"} onClick={() => setFilterType("book")}>Books</FilterButton>
           <FilterButton active={filterType === "article"} onClick={() => setFilterType("article")}>Articles</FilterButton>
           <FilterButton active={filterType === "video"} onClick={() => setFilterType("video")}>Videos</FilterButton>
           <FilterButton active={filterType === "newsletter"} onClick={() => setFilterType("newsletter")}>Newsletters</FilterButton>
        </div>
      </div>

      <ResourcesList search={search} refresh={refresh} filterType={filterType} onUpdate={() => setRefesh((prev) => prev + 1)} />
    </div>
  );
}

const FilterButton = ({ children, active, onClick }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${
      active 
        ? "bg-gray-900 text-white" 
        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
    }`}>
    {children}
  </button>
);

const PER_PAGE = 20;

function ResourcesList({ search, refresh, filterType, onUpdate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setPage(1);
  }, [search, filterType]);

  useEffect(() => {
    fetchAll();
  }, [search, refresh, filterType, page]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      // Use workshop endpoint - normalize data structure
      const { data, ok, total: totalCount } = await api.post("/workshop/search", {
        search: search,
        type: filterType || undefined,
        page,
        per_page: PER_PAGE,
      });

      if (!ok) {
        toast.error("Failed to fetch content");
        return;
      }

      // Normalize workshop data to resource format (name -> title, etc.)
      const normalizedData = (data || []).map(item => ({
        ...item,
        title: item.name || item.title, // Use name as title
        name: item.name, // Keep original name too
        description: item.description || "",
        image: item.image || null,
        author: item.author || null,
        url: item.url || null,
        tags: item.tags || [],
        type: item.type || "other",
        level: item.level || null,
        axe: item.axe || null,
        category: item.category || null,
        _id: item._id,
        updatedAt: item.updated_at || item.created_at,
        createdAt: item.created_at,
        // Component-specific fields
        component_id: item.component_id || null,
        component_images: item.component_images || [],
        features: item.features || [],
        views: item.views || 0,
        github_url: item.github_url || null,
        figma_url: item.figma_url || null,
      }));

      setItems(normalizedData);
      setTotal(totalCount || 0);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch content");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;

  if (items.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
        <HiOutlineDocumentText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <h3 className="text-lg font-medium text-gray-900">No resources found</h3>
        <p className="text-gray-500">Try adjusting your filters or search</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {items.map((item) => (
          <UniversalCard key={item._id || item.id} item={item} onUpdate={onUpdate} />
        ))}
      </div>
      {total > PER_PAGE && (
        <div className="mt-8">
          <Pagination
            total={total}
            per_page={PER_PAGE}
            currentPage={page}
            onNext={() => setPage((p) => p + 1)}
            onPrevious={() => setPage((p) => Math.max(1, p - 1))}
          />
        </div>
      )}
    </>
  );
}

function UniversalCard({ item, onUpdate }) {
  const user = useSelector((state) => state.Auth.user);
  const isWorkshop = item.type === "tutorial";
  const [isEditing, setIsEditing] = useState(false);

  const isComponent = item.type === "component";

  const getIcon = () => {
    if (isWorkshop) return <FaChalkboardTeacher />;
    if (isComponent) return <FaPalette />;
    switch (item.type) {
      case "book": return <FaBook />;
      case "video": return <FaVideo />;
      case "newsletter": return <FaNewspaper />;
      case "article": return <HiOutlineDocumentText />;
      default: return <FaLink />;
    }
  };

  const getLabel = () => {
    if (isWorkshop) return item.type || "Workshop";
    if (isComponent) return "Component";
    return item.type;
  };

  const getTagColor = () => {
     if (isWorkshop) return "bg-purple-100 text-purple-700";
     if (isComponent) return "bg-pink-100 text-pink-700";
     switch (item.type) {
        case "book": return "bg-blue-100 text-blue-700";
        case "video": return "bg-red-100 text-red-700";
        case "newsletter": return "bg-orange-100 text-orange-700";
        case "article": return "bg-green-100 text-green-700";
        default: return "bg-gray-100 text-gray-700";
     }
  }

  const handleDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this resource?")) return;
    
    try {
      const { ok } = await api.remove(`/workshop/${item._id}`);
      if (ok) {
        toast.success("Deleted successfully");
        onUpdate();
      } else {
        toast.error("Failed to delete");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error deleting resource");
    }
  };

  const CardContent = (
      <div className="group bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-xl hover:border-gray-300 transition-all duration-300 flex flex-col h-full overflow-hidden transform hover:-translate-y-1 relative">
        <div className="relative h-48 overflow-hidden bg-gray-50">
           {item.image ? (
             <img 
                src={item.image} 
                alt={item.title} 
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                onError={(e) => {
                    e.target.onerror = null; 
                    e.target.src=placeholder;
                    e.target.classList.add("p-8", "object-contain", "opacity-50"); 
                }}
             />
           ) : (
             <div className={`w-full h-full flex items-center justify-center ${getTagColor()} opacity-20`}>
                 <div className="scale-[3]">{getIcon()}</div>
             </div>
           )}
           
           <div className="absolute top-3 left-3">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold shadow-sm backdrop-blur-md bg-white/90 text-gray-700 capitalize`}>
                {getIcon()}
                {getLabel()}
              </span>
           </div>
           
           {/* Admin Controls */}
           {!isWorkshop && !isComponent && ["admin", "super_admin"].includes(user?.role) && (
             <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
               <button
                 onClick={(e) => {
                   e.preventDefault();
                   e.stopPropagation();
                   setIsEditing(true);
                 }}
                 className="p-2 bg-white/90 hover:bg-white text-gray-600 hover:text-blue-600 rounded-lg shadow-sm backdrop-blur-sm transition-colors">
                 <FaEdit className="w-3 h-3" />
               </button>
               <button
                 onClick={handleDelete}
                 className="p-2 bg-white/90 hover:bg-white text-gray-600 hover:text-red-600 rounded-lg shadow-sm backdrop-blur-sm transition-colors">
                 <FaTrash className="w-3 h-3" />
               </button>
             </div>
           )}

           {/* Hover Overlay */}
           <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
        </div>

        <div className="p-5 flex flex-col flex-grow">
          <h3 className="font-bold text-gray-900 mb-1 line-clamp-2 group-hover:text-primary-600 transition-colors text-lg">
            {item.title}
          </h3>
          
          {item.author && (
            <p className="text-xs font-medium text-gray-500 mb-3 uppercase tracking-wide">{item.author}</p>
          )}

          <p className="text-sm text-gray-600 mb-4 line-clamp-3 flex-grow leading-relaxed">
            {item.description || "No description available."}
          </p>

          <div className="pt-4 mt-auto border-t border-gray-100 flex items-center justify-between text-xs font-medium text-gray-500">
              <span>{item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : "Recently added"}</span>
              <span className="group-hover:translate-x-1 transition-transform duration-200 text-primary-600 flex items-center gap-1">
                {isWorkshop ? "Start Learning" : isComponent ? "View Component" : "View Resource"} 
                <FaExternalLinkAlt className="w-3 h-3" />
              </span>
          </div>
        </div>
      </div>
  );

  const handleComponentClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const componentId = item.component_id || item._id;
    const url = `/selecole/library?component_id=${componentId}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      {isWorkshop ? (
        <Link to={item.url || `/selecole/learn/${item.workshop_id || item._id}`}>{CardContent}</Link>
      ) : isComponent ? (
        <div onClick={handleComponentClick} className="block h-full cursor-pointer" role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleComponentClick(e); }}>
          {CardContent}
        </div>
      ) : (
        <a href={item.url} target="_blank" rel="noopener noreferrer" className="block h-full">
          {CardContent}
        </a>
      )}
      
      {isEditing && (
        <EditResourceModal 
          resource={item} 
          onClose={() => setIsEditing(false)} 
          onUpdated={() => {
            setIsEditing(false);
            onUpdate();
          }} 
        />
      )}
    </>
  );
}

function CreateResourceButton({ onCreated }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-xl font-medium transition-colors shadow-sm">
        <FaPlus className="w-3 h-3" />
        Add Resource
      </button>

      <EditResourceModal onClose={() => setIsOpen(false)} onUpdated={onCreated} isOpen={isOpen} />
    </>
  );
}

function EditResourceModal({ resource, onClose, onUpdated, isOpen = true }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: "",
    type: "article",
    url: "",
    image: "",
    author: "",
    description: "",
  });

  useEffect(() => {
    if (resource) {
      setForm({
        title: resource.title || resource.name || "",
        type: resource.type || "article",
        url: resource.url || "",
        image: resource.image || "",
        author: resource.author || "",
        description: resource.description || "",
      });
    }
  }, [resource]);

  // Extract YouTube ID and set thumbnail
  useEffect(() => {
    if (!form.url) return;
    
    // Regex to find YouTube video ID
    const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = form.url.match(youtubeRegex);

    if (match && match[1]) {
      const videoId = match[1];
      const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
      
      // Automatically set video type if detected
      setForm(prev => {
        const updates = { type: "video" };
        
        // Always update thumbnail if it's empty, or if current image is a YouTube thumbnail
        if (!prev.image || prev.image.includes("img.youtube.com") || prev.image.includes("youtube.com")) {
          updates.image = thumbnailUrl;
        }
        
        return { ...prev, ...updates };
      });
    }
  }, [form.url]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Convert form data to workshop format (name instead of title)
      const workshopData = {
        ...form,
        name: form.title, // Workshop uses 'name' instead of 'title'
      };
      delete workshopData.title; // Remove title field

      let res;
      if (resource) {
        res = await api.put(`/workshop/${resource._id}`, workshopData);
      } else {
        res = await api.post("/workshop", workshopData);
      }
      
      if (res.ok) {
        toast.success(resource ? "Resource updated!" : "Resource added!");
        onUpdated();
      } else {
        toast.error("Operation failed");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !resource) return null; // If purely for create and closed

  return (
    <Modal isOpen={true} onClose={onClose} className="max-w-md w-full p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4">{resource ? "Edit Resource" : "Add New Resource"}</h2>
      <form onSubmit={handleSubmit} className="space-y-4" onClick={(e) => e.stopPropagation()}>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
          <select
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
            className="w-full rounded-lg border-gray-300 focus:ring-primary-500 focus:border-primary-500">
            <option value="article">Article / Link</option>
            <option value="book">Book</option>
            <option value="video">Video</option>
            <option value="newsletter">Newsletter</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
          <input
            required
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full rounded-lg border-gray-300 focus:ring-primary-500 focus:border-primary-500"
            placeholder="e.g. Thinking in Systems"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Author (Optional)</label>
          <input
            type="text"
            value={form.author}
            onChange={(e) => setForm({ ...form, author: e.target.value })}
            className="w-full rounded-lg border-gray-300 focus:ring-primary-500 focus:border-primary-500"
            placeholder="e.g. Donella Meadows"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">URL (Resource Link)</label>
          <input
            type="url"
            value={form.url}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
            className="w-full rounded-lg border-gray-300 focus:ring-primary-500 focus:border-primary-500"
            placeholder="https://..."
          />
          <p className="text-xs text-gray-500 mt-1">Paste a YouTube link to auto-fetch the thumbnail.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Image URL (Cover/Thumbnail)</label>
          <input
            type="url"
            value={form.image}
            onChange={(e) => setForm({ ...form, image: e.target.value })}
            className="w-full rounded-lg border-gray-300 focus:ring-primary-500 focus:border-primary-500"
            placeholder="https://example.com/image.jpg"
          />
          {form.image && (
            <div className="mt-2">
              <img 
                src={form.image} 
                alt="Preview" 
                className="w-full h-32 object-cover rounded-lg border border-gray-200"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            rows={3}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full rounded-lg border-gray-300 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Why is this resource valuable?"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50">
            {loading ? (resource ? "Saving..." : "Adding...") : (resource ? "Save Changes" : "Add Resource")}
          </button>
        </div>
      </form>
    </Modal>
  );
}
